/**
 * ماندهٔ حسابِ پرسنل — بدهکار بالا، بستانکار پایین.
 *
 * جای سه لیستِ بدهکارانِ قبلی را می‌گیرد که هر کدام تعریفِ خودشان را داشتند و
 * با هم نمی‌خواندند. عددِ اینجا از همان سرویسی می‌آید که ریزِ حسابِ هر نفر را
 * می‌سازد، پس دو نما هرگز اختلاف نمی‌کنند.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useRefreshOnShow from "../../utils/useRefreshOnShow";
import { Button, Card, Col, Dropdown, Input, Row, Segmented, Space,
         Statistic, Switch, Tag, Tooltip, Typography, message } from "antd";
import Alert from "../../components/GxAlert";
import { DollarOutlined, DownOutlined, FilePdfOutlined, ReloadOutlined, RightOutlined } from "@ant-design/icons";
import usePdfPreview from "../../utils/usePdfPreview";
import GxTable from "../../components/GxTable";
import VoucherModal from "../../components/VoucherModal";
import { formatMoney } from "../../utils/money";
import { JalaliDateInput, toJalali } from "../../utils/dateUtils";
import { byNum, byStr } from "../../utils/sorters";
import { getBalances } from "../../api/client";

const { Text } = Typography;

const STATUS = {
  debtor: { label: "بدهکار", color: "red" },
  creditor: { label: "بستانکار", color: "green" },
  settled: { label: "تسویه", color: "default" },
};

/** بدهکار قرمز، بستانکار سبز — علامت برای کاربر معنی دارد، نه ریاضی. */
const balanceCell = (v) => {
  if (!v) return <Text type="secondary">۰</Text>;
  const debtor = v > 0;
  return (
    <b style={{ color: debtor ? "#C0392B" : "#107C41" }}>
      {formatMoney(Math.abs(v))}
    </b>
  );
};

export default function BalancesTab({ onOpenLedger, active = true }) {
  const pdf = usePdfPreview();
  // v2.139.0 — لیستِ سادهٔ چاپی (نام + مبلغ؛ بیمه‌ای/غیربیمه‌ای جدا؛ A4/A5 در پیش‌نمایش)
  const SIMPLE = { debtors: "لیست بدهکاران", creditors: "لیست بستانکاران", both: "لیست بدهکاران و بستانکاران" };
  const openSimple = (k) => pdf.open({
    url: "/api/v1/finance/balances/simple.pdf",
    params: { kind: k, ...(asOf ? { as_of: asOf } : {}) },
    title: SIMPLE[k], fallbackName: `${SIMPLE[k]}.pdf`, storageKey: "balances-simple",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [asOf, setAsOf] = useState(null);
  const [q, setQ] = useState("");
  /* پیش‌فرض **روشن**: دفترِ حساب باید همهٔ طرف‌حساب‌ها را نشان دهد، وگرنه کسی
     که حسابش تازه صاف شده ناپدید می‌شود و کاربر فکر می‌کند پاک شده — و برای
     ثبتِ پرداختِ بعدی هم پیدایش نمی‌کند. تسویه‌شده‌ها ته لیست می‌نشینند. */
  const [showSettled, setShowSettled] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getBalances({ as_of: asOf || undefined, include_settled: showSettled })
      .then((r) => setData(r.data))
      .catch(() => message.error("خطا در دریافت مانده‌ها"))
      .finally(() => setLoading(false));
  }, [asOf, showSettled]);

  useEffect(() => { load(); }, [load]);
  // v2.167.0: هر بار که تب/صفحه دوباره دیده شد، خودش تازه شود (نه فقط با «بازخوانی»)
  useRefreshOnShow(load, active);

  /* فیلترِ «چه کسانی» — قبلاً فقط فیلترِ ستونی بود و کسی پیدایش نمی‌کرد.
     دفترِ حساب اول از همه به همین سؤال جواب می‌دهد: «کی بدهکار است، کی
     بستانکار؟» پس باید بالای صفحه و با شمارش باشد. */
  const [kind, setKind] = useState("all");
  const counts = useMemo(() => {
    const all = data?.rows || [];
    return {
      all: all.length,
      debtor: all.filter((r) => r.status === "debtor").length,
      creditor: all.filter((r) => r.status === "creditor").length,
      settled: all.filter((r) => r.status === "settled").length,
    };
  }, [data]);

  const rows = useMemo(() => {
    let all = data?.rows || [];
    if (kind !== "all") all = all.filter((r) => r.status === kind);
    const t = q.trim();
    if (!t) return all;
    return all.filter((r) => String(r.employee_name || "").includes(t)
      || String(r.employee_code || "").includes(t));
  }, [data, q, kind]);

  /* ثبتِ پرداخت **از همین‌جا** با همان سندِ واحدِ پرداخت/دریافت (مبدأ، روش، اثرِ روشن).
     جهت از وضعیتِ خودِ شخص پیشنهاد می‌شود: بدهکار ⇒ دریافت (بازپرداخت)، بستانکار ⇒ پرداخت. */
  const [voucher, setVoucher] = useState(null);
  const openPay = (r) => {
    const debtor = (r.balance || 0) > 0;
    setVoucher({
      employee_id: r.employee_id, kind: debtor ? "receive" : "pay",
      transaction_type: debtor ? "cash_repayment" : "bank_transfer",
      amount: Math.abs(r.balance || 0) || null,
      description: debtor ? "بازپرداخت بدهی" : "پرداخت به پرسنل",
    });
  };

  const cols = [
    {
      title: "پرسنل", dataIndex: "employee_name", width: 190, sorter: byStr("employee_name"),
      render: (v, r) => (
        <Space size={4}>
          <a onClick={() => onOpenLedger?.(r.employee_id)}>{v}</a>
          {r.employee_code && <Text type="secondary" style={{ fontSize: 11 }}>{r.employee_code}</Text>}
        </Space>
      ),
    },
    // v2.132.0: بیمه‌ای/غیربیمه‌ای و سمت — مرتب‌سازی و فیلتر
    { title: "بیمه", dataIndex: "insured_label", width: 100,
      filters: [{ value: "بیمه‌ای", text: "بیمه‌ای" }, { value: "غیربیمه‌ای", text: "غیربیمه‌ای" }],
      onFilter: (v, r) => r.insured_label === v,
      render: (v) => (v ? <Tag color={v === "بیمه‌ای" ? "blue" : "default"} style={{ margin: 0 }}>{v}</Tag> : "—") },
    { title: "سمت", dataIndex: "job_title", width: 120, render: (v) => v || "—" },
    {
      title: "وضعیت", dataIndex: "status", width: 100,
      filters: Object.entries(STATUS).map(([v, s]) => ({ value: v, text: s.label })),
      onFilter: (v, r) => r.status === v,
      render: (v) => <Tag color={STATUS[v].color}>{STATUS[v].label}</Tag>,
    },
    {
      title: "مانده (ریال)", dataIndex: "balance", width: 150, align: "left",
      sorter: byNum("balance"), render: balanceCell,
    },
    {
      title: "بدهیِ باز", dataIndex: "open_debt", width: 130, align: "left",
      sorter: byNum("open_debt"),
      render: (v) => (v ? formatMoney(v) : <Text type="secondary">—</Text>),
    },
    /* جمع‌های **کامل** (شاملِ اصلِ بدهی و بازپرداختش) — نه فقط تراکنش‌ها.
       اتحادِ زیر همیشه برقرار است و ردیف را خودتوضیح می‌کند:
           جمع بدهکار − جمع بستانکار = مانده */
    { title: "جمع بدهکار", dataIndex: "total_debit", width: 130, align: "left",
      sorter: byNum("total_debit"),
      render: (v, r) => formatMoney(v ?? r.txn_debit) },
    { title: "جمع بستانکار", dataIndex: "total_credit", width: 130, align: "left",
      sorter: byNum("total_credit"),
      render: (v, r) => formatMoney(v ?? r.txn_credit) },
    {
      title: "آخرین تراکنش", dataIndex: "last_activity_at", width: 120,
      sorter: byStr("last_activity_at"),
      render: (v) => (v ? toJalali(v) : <Text type="secondary">—</Text>),
    },
    {
      title: "اقدام", width: 130, fixed: "left",
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="ریزِ حساب">
            <Button size="small" icon={<RightOutlined />}
              onClick={() => onOpenLedger?.(r.employee_id)} />
          </Tooltip>
          {r.status !== "settled" && (
            <Button size="small" type="primary" ghost icon={<DollarOutlined />}
              onClick={() => openPay(r)}>ثبت پرداخت</Button>
          )}
        </Space>
      ),
    },
  ];

  const t = data?.totals;
  return (
    <Card
      title="ماندهٔ حساب پرسنل"
      extra={
        <Space wrap>
          <Segmented value={kind} onChange={setKind} options={[
            { value: "all", label: `همه (${counts.all})` },
            { value: "debtor", label: `بدهکار (${counts.debtor})` },
            { value: "creditor", label: `بستانکار (${counts.creditor})` },
            { value: "settled", label: `تسویه (${counts.settled})` },
          ]} />
          <Input.Search allowClear placeholder="جست‌وجوی نام" value={q}
            onChange={(e) => setQ(e.target.value)} style={{ width: 170 }} />
          <Space size={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>تا تاریخ</Text>
            <JalaliDateInput value={asOf} onChange={setAsOf} allowClear style={{ width: 130 }} />
          </Space>
          <Space size={4}>
            <Text type="secondary" style={{ fontSize: 12 }}>تسویه‌شده‌ها</Text>
            <Switch size="small" checked={showSettled} onChange={setShowSettled} />
          </Space>
          <Dropdown trigger={["click"]} menu={{
            items: [
              { key: "debtors", label: "بدهکاران (نام + مبلغ)" },
              { key: "creditors", label: "بستانکاران (نام + مبلغ)" },
              { key: "both", label: "هر دو در یک فایل" },
            ],
            onClick: ({ key }) => openSimple(key),
          }}>
            <Button icon={<FilePdfOutlined />}>لیستِ ساده <DownOutlined /></Button>
          </Dropdown>
          <Tooltip title="بازخوانی">
            <Button icon={<ReloadOutlined />} onClick={load} aria-label="بازخوانی" />
          </Tooltip>
        </Space>
      }>
      {pdf.node}
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="بدهکار یعنی پرسنل به کارخانه بدهکار است؛ بستانکار یعنی کارخانه به او."
        description="ترتیب: اول بدهکارها (بزرگ‌ترین بالا)، بعد بستانکارها، و آخر کسانی که حسابشان صاف است. «جمع بدهکار منهای جمع بستانکار» دقیقاً همان «مانده» است. روی نام کلیک کنید تا ریزِ تراکنش‌ها و بدهی‌هایش با تاریخ باز شود." />

      {t && (
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col xs={12} md={6}>
            <Card size="small"><Statistic title="بدهکار" value={t.debtors_count}
              suffix="نفر" valueStyle={{ color: "#C0392B" }} /></Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small"><Statistic title="جمع بدهکاری (ریال)"
              value={formatMoney(t.total_debtor_amount)} valueStyle={{ color: "#C0392B" }} /></Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small"><Statistic title="بستانکار" value={t.creditors_count}
              suffix="نفر" valueStyle={{ color: "#107C41" }} /></Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small"><Statistic title="جمع بستانکاری (ریال)"
              value={formatMoney(t.total_creditor_amount)} valueStyle={{ color: "#107C41" }} /></Card>
          </Col>
        </Row>
      )}

      {/* خروجیِ CSV خودِ GxTable — همان دادهٔ فیلترشدهٔ روی صفحه */}
      <GxTable layoutId="finance-balances" dataSource={rows} columns={cols}
        gxExportName="employee-balances"
        rowKey="employee_id" size="small" loading={loading}
        pagination={{ pageSize: 30 }} scroll={{ x: "max-content" }} />

      <VoucherModal open={!!voucher} initial={voucher || {}} onClose={() => setVoucher(null)}
        onSaved={load} />
    </Card>
  );
}
